import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/sleek";

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/portfolio")
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get("/portfolio-stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  const cats = useMemo(() => ["All", ...Array.from(new Set(items.map((i) => i.category)))], [items]);
  const filtered = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28">
      <div className="text-xs tracking-[0.3em] uppercase text-zinc-500">Portfolio</div>
      <h1 className="font-display text-5xl md:text-7xl font-light tracking-tight mt-4 max-w-5xl leading-[1.0]">
        Built to Last. Attention to Detail.<br />Designed Around You.
      </h1>

      {stats && (
        <div
          className="mt-10 flex flex-wrap items-baseline gap-x-6 gap-y-3 border-t border-b border-zinc-200 py-5"
          data-testid="portfolio-stats-strip"
        >
          <Stat number="27+" label="Years Experience" />
          <Divider />
          <Stat number="100%" label="Sleek Quality" />
          <Divider />
          <Stat number="MetroWest" label="Boston-area, MA" wide />
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-10">
        {cats.map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            className={`text-xs tracking-[0.2em] uppercase px-4 py-2 border ${filter === c ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 text-zinc-700 hover:border-zinc-950"}`}
            data-testid={`portfolio-filter-${c.toLowerCase()}`}
          >
            {c}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
        {loading
          ? Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="animate-pulse" data-testid="portfolio-skeleton">
                <div className="aspect-[4/5] bg-zinc-100" />
                <div className="mt-5 space-y-3">
                  <div className="h-2 w-16 bg-zinc-200" />
                  <div className="h-6 w-3/4 bg-zinc-200" />
                  <div className="h-3 w-full bg-zinc-100" />
                  <div className="h-3 w-5/6 bg-zinc-100" />
                </div>
              </div>
            ))
          : filtered.map((p) => (
          <Link
            key={p.id}
            to={`/portfolio/${p.id}`}
            className="group block"
            data-testid={`portfolio-item-${p.id}`}
          >
            <div className="overflow-hidden aspect-[4/5] bg-zinc-100">
              <img src={p.image} alt={p.title} style={p.image_pos ? { objectPosition: p.image_pos } : undefined} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
            </div>
            <div className="mt-5">
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">{p.category}</div>
              <div className="font-display text-2xl mt-2 group-hover:underline decoration-1 underline-offset-4">{p.title}</div>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{p.summary}</p>
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mt-4 group-hover:text-zinc-950 transition">
                View project →
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="border-t border-zinc-200 mt-20 pt-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="font-display text-3xl md:text-4xl font-light">See yours next.</div>
          <p className="text-zinc-600 mt-2 max-w-xl">Tell us about the room you&apos;ve been thinking about. We&apos;ll respond within one business day.</p>
        </div>
        <Link to="/booking" className="bg-zinc-950 text-white px-7 py-4 text-sm hover:bg-zinc-800 transition w-fit">Book a consultation</Link>
      </div>
    </div>
  );
}

function Stat({ number, label, wide = false }) {
  return (
    <div className="flex items-baseline gap-3">
      <div className={`font-display font-light leading-none ${wide ? "text-3xl md:text-4xl" : "text-4xl md:text-5xl"}`}>
        {number}
      </div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">{label}</div>
    </div>
  );
}

function Divider() {
  return <div className="h-6 w-px bg-zinc-300 self-center hidden sm:block" aria-hidden="true" />;
}
